import { useEffect, useState } from "react";

/**
 * Renders a transient "+N" badge whenever `value` increases.
 * Pure visual — no recompute on parent.
 */
export function PulseDeltaBadge({ value, suffix = "" }: { value: number; suffix?: string }) {
  const [prev, setPrev] = useState(value);
  const [delta, setDelta] = useState<number | null>(null);

  useEffect(() => {
    if (value > prev) {
      const d = Math.round(value - prev);
      if (d > 0) {
        setDelta(d);
        const t = window.setTimeout(() => setDelta(null), 1600);
        return () => window.clearTimeout(t);
      }
    }
    setPrev(value);
  }, [value, prev]);

  if (delta === null) return null;
  return (
    <span
      key={delta + "-" + Date.now()}
      className="pointer-events-none absolute -right-1 -top-2 select-none rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-black text-primary-foreground shadow-[0_0_18px_var(--shadow-glow,rgba(255,80,80,.6))] animate-fade-in"
      style={{ animation: "fade-in 0.25s ease-out, fade-out 1.3s ease-out 0.3s forwards" }}
    >
      +{delta}
      {suffix}
    </span>
  );
}

/**
 * Animated count-up that eases toward `value`. ~600ms transition,
 * no library dependency. Renders an integer.
 */
export function AnimatedNumber({ value, className }: { value: number; className?: string }) {
  const [display, setDisplay] = useState(value);
  useEffect(() => {
    const from = display;
    const to = value;
    if (from === to) return;
    const start = performance.now();
    const dur = 600;
    let raf = 0;
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(from + (to - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return <span className={className}>{display.toLocaleString()}</span>;
}